import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiMaximize, FiMinimize, FiDownload, FiChevronDown, FiChevronRight,
  FiSearch, FiX, FiCalendar, FiClock, FiDollarSign, FiLayers,
  FiCheckCircle, FiAlertTriangle, FiGitBranch, FiCrosshair,
  FiMaximize2, FiInfo, FiTrendingUp, FiUsers, FiBriefcase,
  FiSettings, FiActivity, FiCpu, FiDatabase, FiZap, FiShield,
  FiPlay, FiRefreshCw, FiArrowRight, FiCheck, FiPackage
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';

// ==================== IMPLEMENTATION PLAN GENERATOR ====================

const generateImplementationPlan = (components) => {
  const plan = {
    phases: [],
    integrationPoints: [],
    dependencies: [],
    risks: [],
    milestones: []
  };
  
  // Group components by deployment phase
  const phaseGroups = {
    'Phase 1: Foundation': components.filter(c => ['layer1', 'layer2'].includes(c.layerId)),
    'Phase 2: Data Infrastructure': components.filter(c => ['layer3', 'layer4'].includes(c.layerId)),
    'Phase 3: Intelligence': components.filter(c => ['layer5', 'layer6', 'layer7'].includes(c.layerId)),
    'Phase 4: Execution': components.filter(c => ['layer8', 'layer9'].includes(c.layerId)),
    'Phase 5: Application': components.filter(c => ['layer10'].includes(c.layerId)),
    'Phase 6: Operations': components.filter(c => ['layer11', 'layer12'].includes(c.layerId))
  };
  
  Object.entries(phaseGroups).forEach(([phaseName, phaseComponents], index) => {
    if (phaseComponents.length > 0) {
      plan.phases.push({
        id: `phase-${index + 1}`,
        name: phaseName,
        components: phaseComponents,
        duration: getPhaseDuration(phaseComponents),
        cost: getPhaseCost(phaseComponents),
        dependencies: index > 0 ? [plan.phases[index - 1]?.name] : [],
        tasks: getPhaseTasks(phaseComponents),
        deliverables: getPhaseDeliverables(phaseComponents)
      });
    }
  });
  
  // Integration points
  plan.integrationPoints = [
    { name: 'API Integration', components: components.filter(c => c.tags?.includes('api')), status: 'pending' },
    { name: 'Database Integration', components: components.filter(c => c.tags?.includes('database')), status: 'pending' },
    { name: 'Security Integration', components: components.filter(c => c.tags?.includes('security')), status: 'pending' },
    { name: 'Monitoring Integration', components: components.filter(c => c.tags?.includes('monitoring')), status: 'pending' }
  ].filter(ip => ip.components.length > 0);
  
  // Dependencies
  plan.dependencies = generateDependencies(components);
  
  // Risks
  plan.risks = generateImplementationRisks(components);
  
  // Milestones
  plan.milestones = plan.phases.map((phase, index) => ({
    name: `${phase.name} Complete`,
    duration: phase.duration,
    dependsOn: index > 0 ? plan.phases[index - 1].name : null
  }));
  
  return plan;
};

const getPhaseDuration = (components) => {
  const count = components.length;
  if (count > 10) return '3-4 weeks';
  if (count > 5) return '2-3 weeks';
  return '1-2 weeks';
};

const getPhaseCost = (components) => {
  return components.reduce((sum, c) => sum + (c.cost?.high || 0), 0);
};

const getPhaseTasks = (components) => {
  return components.map(comp => ({
    task: `Deploy and configure ${comp.name}`,
    component: comp,
    duration: '1-2 days',
    assignee: 'DevOps Engineer',
    status: 'pending'
  }));
};

const getPhaseDeliverables = (components) => {
  return components.map(comp => `${comp.name} operational and verified`);
};

const generateDependencies = (components) => {
  const deps = [];
  const layerOrder = ['layer1', 'layer2', 'layer3', 'layer4', 'layer5', 'layer6', 'layer7', 'layer8', 'layer9', 'layer10', 'layer11', 'layer12'];
  
  layerOrder.forEach((layer, index) => {
    const layerComponents = components.filter(c => c.layerId === layer);
    if (layerComponents.length > 0 && index < layerOrder.length - 1) {
      const nextLayer = layerOrder[index + 1];
      const nextComponents = components.filter(c => c.layerId === nextLayer);
      if (nextComponents.length > 0) {
        deps.push({
          from: layer,
          to: nextLayer,
          description: `Layer ${layer.slice(5)} components must be deployed before Layer ${nextLayer.slice(5)}`
        });
      }
    }
  });
  
  return deps;
};

const generateImplementationRisks = (components) => {
  const risks = [];
  
  if (components.some(c => c.tags?.includes('gpu'))) {
    risks.push({
      risk: 'GPU infrastructure provisioning delays',
      impact: 'High',
      probability: 'Medium',
      mitigation: 'Pre-order hardware or use cloud GPU instances'
    });
  }
  
  if (components.some(c => c.tags?.includes('security'))) {
    risks.push({
      risk: 'Security compliance approval delays',
      impact: 'High',
      probability: 'Medium',
      mitigation: 'Engage compliance team early'
    });
  }
  
  if (components.length > 30) {
    risks.push({
      risk: 'Integration complexity',
      impact: 'High',
      probability: 'Medium',
      mitigation: 'Use phased rollout approach'
    });
  }
  
  return risks;
};

// ==================== MAIN COMPONENT ====================

const ImplementationView = () => {
  const { state, dispatch } = useSolution();
  const [expandedPhases, setExpandedPhases] = useState(new Set());
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [showIntegration, setShowIntegration] = useState(true);
  const [showDependencies, setShowDependencies] = useState(true);
  const [showRisks, setShowRisks] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const mainContainerRef = useRef(null);
  
  const implementationPlan = useMemo(() => generateImplementationPlan(state.selectedComponents), [state.selectedComponents]);
  
  const toggleFullscreen = useCallback(() => {
    const element = mainContainerRef.current;
    if (!element) return;
    if (!document.fullscreenElement) {
      element.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);
  
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen]);
  
  const togglePhase = useCallback((phaseId) => {
    setExpandedPhases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phaseId)) newSet.delete(phaseId);
      else newSet.add(phaseId);
      return newSet;
    });
  }, []);
  
  const expandAll = useCallback(() => {
    setExpandedPhases(new Set(implementationPlan.phases.map(p => p.id)));
  }, [implementationPlan.phases]);
  
  const collapseAll = useCallback(() => {
    setExpandedPhases(new Set());
  }, []);
  
  return (
    <div 
      ref={mainContainerRef}
      className={`h-full overflow-y-auto ${isFullscreen ? 'bg-white' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}
      style={isFullscreen ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}}
    >
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
            <FiBriefcase className="w-8 h-8 text-blue-600" />
            Implementation & Integration Plan
          </h2>
          <p className="text-gray-600">
            Step-by-step implementation guide for {state.selectedComponents.length} components
          </p>
        </div>
        
        {/* Controls */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <button onClick={expandAll} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold">Expand All</button>
            <button onClick={collapseAll} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold">Collapse All</button>
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={() => setShowIntegration(!showIntegration)} className={`px-4 py-2 rounded-lg text-sm font-semibold ${showIntegration ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}>
              Integration: {showIntegration ? 'On' : 'Off'}
            </button>
            <button onClick={() => setShowDependencies(!showDependencies)} className={`px-4 py-2 rounded-lg text-sm font-semibold ${showDependencies ? 'bg-teal-100 text-teal-700' : 'bg-gray-100'}`}>
              Dependencies: {showDependencies ? 'On' : 'Off'}
            </button>
            <button onClick={() => setShowRisks(!showRisks)} className={`px-4 py-2 rounded-lg text-sm font-semibold ${showRisks ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}>
              Risks: {showRisks ? 'On' : 'Off'}
            </button>
            <button onClick={toggleFullscreen} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
              {isFullscreen ? <FiMinimize className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
        
        {/* Implementation Phases */}
        <div className="space-y-4 mb-8">
          {implementationPlan.phases.map((phase, index) => {
            const isExpanded = expandedPhases.has(phase.id);
            const isSelected = selectedPhase === phase.id;
            
            return (
              <motion.div
                key={phase.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-2xl shadow-sm border-2 transition-all ${
                  isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                }`}
              >
                {/* Phase Header */}
                <button
                  onClick={() => {
                    togglePhase(phase.id);
                    setSelectedPhase(phase.id);
                  }}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl"
                >
                  <div className="flex items-center gap-4">
                    <span className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </span>
                    <div className="text-left">
                      <div className="font-bold text-gray-900 text-lg">{phase.name}</div>
                      <div className="text-xs text-gray-500">
                        {phase.components.length} components • {phase.duration}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-green-600">
                      ${phase.cost.toLocaleString()}
                    </span>
                    {isExpanded ? <FiChevronDown className="w-5 h-5" /> : <FiChevronRight className="w-5 h-5" />}
                  </div>
                </button>
                
                {/* Expanded Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
                        {/* Tasks */}
                        <h4 className="font-semibold text-gray-700 mb-2 text-sm">Implementation Tasks</h4>
                        <div className="space-y-2 mb-4">
                          {phase.tasks.map((task, taskIndex) => (
                            <div key={taskIndex} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-100">
                              <FiCheckCircle className="w-4 h-4 text-gray-300" />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">{task.task}</div>
                                <div className="text-xs text-gray-500">{task.duration} • {task.assignee}</div>
                              </div>
                              <button
                                onClick={() => dispatch({ type: 'SHOW_COMPONENT_DETAIL', payload: task.component })}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Details
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Deliverables */}
                        <h4 className="font-semibold text-gray-700 mb-2 text-sm">Deliverables</h4>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {phase.deliverables.map((deliverable, dIndex) => (
                            <span key={dIndex} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                              ✓ {deliverable}
                            </span>
                          ))}
                        </div>
                        
                        {/* Dependencies */}
                        {phase.dependencies.length > 0 && (
                          <div className="mb-2">
                            <h4 className="font-semibold text-gray-700 mb-1 text-sm">Depends On</h4>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <FiArrowRight className="w-4 h-4" />
                              {phase.dependencies.join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
        
        {/* Integration Points */}
        {showIntegration && implementationPlan.integrationPoints.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">🔗 Integration Points</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {implementationPlan.integrationPoints.map((ip, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl">
                  <div className="font-semibold text-gray-900">{ip.name}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {ip.components.length} components to integrate
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {ip.components.slice(0, 5).map(comp => (
                      <span key={comp.id} className="px-2 py-0.5 bg-white rounded-full text-xs text-gray-600">
                        {comp.icon} {comp.name}
                      </span>
                    ))}
                    {ip.components.length > 5 && (
                      <span className="px-2 py-0.5 bg-white rounded-full text-xs text-gray-500">
                        +{ip.components.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Dependencies */}
        {showDependencies && implementationPlan.dependencies.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">🔗 Layer Dependencies</h3>
            <div className="space-y-2">
              {implementationPlan.dependencies.map((dep, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                  <span className="font-semibold text-blue-600">Layer {dep.from.slice(5)}</span>
                  <FiArrowRight className="w-4 h-4 text-gray-400" />
                  <span className="font-semibold text-purple-600">Layer {dep.to.slice(5)}</span>
                  <span className="text-gray-500">- {dep.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Risks */}
        {showRisks && implementationPlan.risks.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">⚠️ Implementation Risks</h3>
            <div className="space-y-3">
              {implementationPlan.risks.map((risk, index) => (
                <div key={index} className="p-4 bg-red-50 border border-red-100 rounded-xl">
                  <div className="font-semibold text-red-800">{risk.risk}</div>
                  <div className="text-sm text-red-600 mt-1">
                    Impact: {risk.impact} • Probability: {risk.probability}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    <strong>Mitigation:</strong> {risk.mitigation}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {implementationPlan.phases.length === 0 && (
          <div className="text-center p-12 bg-white rounded-2xl border border-gray-200">
            <div className="text-6xl mb-4">📋</div>
            <p className="text-lg font-semibold text-gray-700">No components selected</p>
            <p className="text-gray-500">Select components to view implementation plan</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImplementationView;